import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Dialog, 
  Button, 
  Paper,
  CircularProgress,
  Fade,
  Backdrop,
  Divider,
  Slide,
  IconButton,
  Tooltip,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import { 
  Warning as WarningIcon,
  LocalHospital as MedicalIcon,
  LocationOn as LocationIcon,
  PhoneInTalk as PhoneIcon,
  NotificationsActive as AlertIcon,
  Fingerprint as FingerprintIcon,
  Cancel as CancelIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import FingerprintAuth from '../auth/FingerprintAuth';

const EmergencyAlertScreen = ({ open, onClose, alertDetails }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [needsMedical, setNeedsMedical] = useState(null);
  const [fingerprintDialogOpen, setFingerprintDialogOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [flashingState, setFlashingState] = useState(true);
  const [dismissedMessages, setDismissedMessages] = useState([]);

  // Messages to display sequentially
  const emergencyMessages = [
    {
      title: "TRACKING ACTIVE",
      message: "We are actively tracking your asset's location in real-time.",
      icon: <LocationIcon fontSize="large" />,
      delay: 0
    },
    {
      title: "AUTHORITIES NOTIFIED",
      message: "Local security personnel have been dispatched to your location.",
      icon: <LocationIcon fontSize="large" />,
      delay: 3000
    },
    {
      title: "CONTACTING YOU",
      message: "Our emergency response team will call you within the next 2 minutes.",
      icon: <PhoneIcon fontSize="large" />,
      delay: 6000
    }
  ];

  // Create flashing effect
  useEffect(() => {
    if (!open) return;
    
    const flashingInterval = setInterval(() => {
      setFlashingState(prev => !prev);
    }, 500); // Flash every 500ms
    
    return () => clearInterval(flashingInterval);
  }, [open]);

  // Progress through messages
  useEffect(() => {
    if (!open || activeStep >= emergencyMessages.length) return;
    
    const timer = setTimeout(() => {
      setActiveStep(prev => prev + 1);
    }, emergencyMessages[activeStep].delay);
    
    return () => clearTimeout(timer);
  }, [activeStep, open, emergencyMessages]);

  const handleMedicalResponse = (needs) => {
    setNeedsMedical(needs);
    // If medical assistance is needed, show this as an additional message
    if (needs) {
      setTimeout(() => {
        // This would trigger additional emergency medical services in a real app
        console.log("Medical assistance requested");
      }, 1000);
    }
  };

  const handleCancelEmergency = () => {
    // Skip confirmation and go straight to fingerprint authentication
    setFingerprintDialogOpen(true);
  };
  
  // No longer needed as we're going straight to fingerprint
  const handleCancelConfirm = () => {
    setCancelConfirmOpen(false);
    setFingerprintDialogOpen(true);
  };
  
  const handleCancelDismiss = () => {
    setCancelConfirmOpen(false);
  };

  const handleFingerprintSuccess = () => {
    // Close the fingerprint dialog first
    setFingerprintDialogOpen(false);
    
    // Add a small delay before closing the emergency screen
    setTimeout(() => {
      // Force close the emergency screen
      if (typeof onClose === 'function') {
        onClose();
      }
    }, 300);
  };
  
  const handleDismissMessage = (index) => {
    setDismissedMessages(prev => [...prev, index]);
  };

  if (!open) return null;

  return (
    <>
      <Backdrop
        sx={{
          zIndex: 9999,
          backgroundColor: 'rgba(211, 47, 47, 0.95)',
          flexDirection: 'column',
          transition: 'background-color 0.5s ease',
          overflow: 'auto',
        }}
        open={open}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Fixed Cancel Button - Always visible at the top */}
        <Box sx={{ position: 'fixed', top: 16, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 10001 }}>
          <Button
            variant="contained"
            color="inherit"
            onClick={handleCancelEmergency}
            sx={{ 
              bgcolor: 'white', 
              color: 'error.main',
              '&:hover': {
                bgcolor: 'white'
              },
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: 50,
              boxShadow: 3,
              textTransform: 'none',
              minWidth: 250
            }}
          >
            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
              <FingerprintIcon sx={{ mr: 1, color: 'error.main' }} />
              CANCEL EMERGENCY
            </Box>
          </Button>
        </Box>
        
        <Box 
          sx={{ 
            width: '100%', 
            minHeight: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: 8,
            pb: 10,
            px: 2,
            position: 'relative'
          }}
        >
          {/* Emergency Header */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mt: 10,
              mb: 4,
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.05)' },
                '100%': { transform: 'scale(1)' },
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <WarningIcon sx={{ fontSize: 30, color: 'white', mr: 2 }} />
              <Typography variant="h4" component="h1" sx={{ color: 'white', fontWeight: 700 }}>
                EMERGENCY ALERT
              </Typography>
            </Box>
          </Box>
          
          {/* Alert Details */}
          <Card 
            elevation={2} 
            sx={{ 
              width: '100%', 
              maxWidth: 500, 
              mb: 4, 
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'white'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Alert Type:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                    {alertDetails?.type?.replace('_', ' ') || 'MISSING'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Asset:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                    {alertDetails?.assetName || 'Diamond Necklace'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Time:
                  </Typography>
                  <Typography variant="body1">
                    {alertDetails?.timestamp 
                      ? new Date(alertDetails.timestamp).toLocaleString() 
                      : new Date().toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          
          {/* Sequential Messages */}
          <Stack spacing={2} sx={{ width: '100%', maxWidth: 500, mb: 4 }}>
            {emergencyMessages.slice(0, activeStep)
              .filter((_, index) => !dismissedMessages.includes(index))
              .map((msg, index) => {
                const originalIndex = emergencyMessages.slice(0, activeStep).findIndex((_, i) => 
                  !dismissedMessages.includes(i) || i === index
                );
                return (
                  <Slide direction="up" in={true} key={`message-${originalIndex}`} timeout={500}>
                    <Card 
                      elevation={2} 
                      sx={{ 
                        borderRadius: 3,
                        bgcolor: 'white',
                        position: 'relative',
                        overflow: 'visible'
                      }}
                    >
                      <IconButton 
                        size="small" 
                        sx={{ 
                          position: 'absolute', 
                          top: 8, 
                          right: 8,
                          zIndex: 1,
                          color: 'text.secondary'
                        }}
                        onClick={() => handleDismissMessage(originalIndex)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      
                      <CardContent sx={{ display: 'flex', p: 3 }}>
                        <Box sx={{ 
                          mr: 2, 
                          color: 'error.main', 
                          display: 'flex',
                          alignItems: 'flex-start',
                          pt: 0.5
                        }}>
                          {msg.icon}
                        </Box>
                        <Box sx={{ flex: 1, pr: 3 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {msg.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {msg.message}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Slide>
                );
              })}
          </Stack>
          
          {/* Medical Assistance Question - shown after all messages */}
          {activeStep >= emergencyMessages.length && needsMedical === null && (
            <Slide direction="up" in={true} timeout={500}>
              <Card 
                elevation={2} 
                sx={{ 
                  borderRadius: 3,
                  width: '100%',
                  maxWidth: 500,
                  mb: 4,
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MedicalIcon sx={{ color: 'error.main', mr: 1.5, fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Medical Assistance
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    Do you or anyone with you require medical attention?
                  </Typography>
                  
                  <Stack direction="row" spacing={2}>
                    <Button 
                      variant="contained" 
                      color="error" 
                      fullWidth
                      onClick={() => handleMedicalResponse(true)}
                      sx={{ py: 1.5, borderRadius: 2 }}
                    >
                      Yes, Need Medical Help
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      fullWidth
                      onClick={() => handleMedicalResponse(false)}
                      sx={{ py: 1.5, borderRadius: 2 }}
                    >
                      No, We're Safe
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Slide>
          )}
          
          {/* Medical Response Confirmation */}
          {needsMedical !== null && (
            <Slide direction="up" in={true} timeout={500}>
              <Card 
                elevation={2} 
                sx={{ 
                  borderRadius: 3,
                  width: '100%',
                  maxWidth: 500,
                  mb: 4,
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ 
                  p: 3,
                  bgcolor: needsMedical ? 'error.50' : 'success.50'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MedicalIcon sx={{ 
                      color: needsMedical ? 'error.main' : 'success.main', 
                      mr: 1.5, 
                      fontSize: 24 
                    }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {needsMedical 
                        ? 'Medical Services Dispatched' 
                        : 'Medical Status Confirmed'}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1">
                    {needsMedical 
                      ? 'Emergency medical services have been notified and are being dispatched to your location. Please stay where you are if possible.' 
                      : 'Thank you for confirming. Our security team will still respond to your emergency alert.'}
                  </Typography>
                </CardContent>
              </Card>
            </Slide>
          )}
          
          {/* Bottom Cancel Button - Fixed at bottom */}
          <Box sx={{ 
            position: 'fixed', 
            bottom: 20, 
            left: 0, 
            right: 0, 
            display: 'flex', 
            justifyContent: 'center',
            zIndex: 10001
          }}>
            <Button
              variant="contained"
              color="inherit"
              size="large"
              onClick={handleCancelEmergency}
              sx={{ 
                bgcolor: 'white', 
                color: 'error.main',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)'
                },
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 50,
                boxShadow: 3,
                textTransform: 'none',
                minWidth: 250
              }}
            >
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                <FingerprintIcon sx={{ mr: 1, color: 'error.main' }} />
                CANCEL EMERGENCY
              </Box>
            </Button>
          </Box>
        </Box>
      </Backdrop>
      
      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleCancelDismiss}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
        sx={{ zIndex: 10002 }} /* Ensure dialog appears above the backdrop */
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', py: 2 }}>
          Cancel Emergency Alert
        </DialogTitle>
        <DialogContent sx={{ py: 3, mt: 1 }}>
          <DialogContentText>
            Are you sure you want to cancel this emergency alert? This will notify security personnel that the emergency is resolved.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={handleCancelDismiss} 
            variant="outlined"
            color="inherit"
            sx={{ flex: 1, mr: 2 }}
          >
            Keep Alert Active
          </Button>
          <Button 
            onClick={handleCancelConfirm} 
            variant="contained" 
            color="error"
            startIcon={<FingerprintIcon />}
            sx={{ flex: 1 }}
          >
            Proceed to Authentication
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Fingerprint Authentication Dialog */}
      <FingerprintAuth 
        open={fingerprintDialogOpen}
        onSuccess={handleFingerprintSuccess}
        onCancel={() => setFingerprintDialogOpen(false)}
        title="Authenticate to Cancel Emergency"
      />
    </>
  );
};

export default EmergencyAlertScreen;
